import {
  AddIcon,
  AddUsersIcon,
  Button,
  type ContextMenuAction,
  CopyIcon,
  EditIcon,
  FlexContent,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  VisibilityIcon,
  useDebounce,
} from "@rothoblaas/design-system";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/ui/Icon";
import SharingIcon from "../../components/ui/SharingIcon";
import Table from "../../components/ui/Table";
import { getCategoryIcon } from "../../constants/categoryIcons";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchAdminCalculation, SearchCalculation } from "../../http/types/calculations";
import { useCalculationsApi } from "../../http/useCalculations";
import { useModulesApi } from "../../http/useModules";
import { PaginationQuery } from "../../types";
import { canManageModuleCalculations, formatTimeStamp } from "../../utils";
import { getDeleteAction } from "../../utils/rowActions";
import { CalculationForm } from "./CalculationForm";
import styles from "./Calculations.module.css";
import CalculationSharingDialog from "./CalculationSharingDialog";
import DuplicateCalculationDialog from "./DuplicateCalculationDialog";

type CalculationsProps = {
  isAdmin?: boolean;
  moduleId?: string; // if set it means we are in a single module app
};

const Calculations: FC<CalculationsProps> = ({ isAdmin = false, moduleId }: CalculationsProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const moduleAborter = useRef(new AbortController());
  const calculationsApi = useCalculationsApi();
  const modulesApi = useModulesApi();

  // Only single-module apps pass a fixed moduleId; everywhere else module choice (and therefore
  // visibility) is handled by CalculationForm's own module picker, which already filters out
  // hidden/restricted modules.
  const { data: moduleData } = modulesApi.getModule(moduleId, moduleAborter.current.signal);
  const canCreateInModule = !moduleId || canManageModuleCalculations(moduleData?.visibility, auth.identity?.email);

  const debouncedValue = useDebounce(filters.state.q);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModalCreateCalculation, setShowModalCreateCalculation] = useState(false);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const urlCalculations = isAdmin ? "/admin/calculations" : "/my-calculations";
  // Depending on the isAdmin prop, we use either the admin or regular calculations API hook
  const { data, error } = isAdmin
    ? calculationsApi.useAdminCalculations(query, aborter.current.signal, moduleId)
    : calculationsApi.useCalculations(query, aborter.current.signal, moduleId);

  const [calculationToDelete, setCalculationToDelete] = useState<SearchCalculation | SearchAdminCalculation>();
  const [calculationToDuplicate, setCalculationToDuplicate] = useState<SearchCalculation | SearchAdminCalculation>();
  const [calculationToShare, setCalculationToShare] = useState<SearchCalculation | SearchAdminCalculation>();

  const isInitialized = useInitializer(data || error);
  if (!data && !error && !isInitialized) {
    return;
  }

  // Define row actions based on user permissions and whether the user is an admin
  const rowActions: (item: SearchCalculation | SearchAdminCalculation) => ContextMenuAction[] = (item) => {
    const userActions = [
      {
        label: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
        onClick: () => navigate(`${urlCalculations}/${item.id}`),
        icon: <Icon glyph={item.canEdit ? EditIcon : VisibilityIcon} />,
      },
      {
        label: i18n.translation.projects.editSharings,
        onClick: () => (isAdmin || item.owner?.id === auth.identity?.id) && setCalculationToShare(item),
        icon: <Icon glyph={AddUsersIcon} />,
        disabled: !isAdmin && item.owner?.id !== auth.identity?.id,
      },
      {
        label: i18n.translation.common.duplicate,
        onClick: () => setCalculationToDuplicate(item),
        icon: <Icon glyph={CopyIcon} />,
        disabled: !isAdmin && !item.canManageModule,
      },
      getDeleteAction(
        i18n,
        () => {
          (isAdmin || item.owner.id === auth.identity?.id) && setCalculationToDelete(item);
          return item.owner.id !== auth.identity?.id;
        },
        true,
        !(isAdmin || item.owner.id === auth.identity?.id),
      ),
    ];

    return userActions;
  };

  return (
    <>
      <Header
        title={i18n.translation.calculations.plural}
        path={{ display: isAdmin ? i18n.translation.calculations.all : i18n.translation.calculations.my }}
        error={error}
        buttons={
          canCreateInModule && (
            <Button onClick={() => setShowModalCreateCalculation(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.calculations.createNew}
            </Button>
          )
        }
      />

      <FlexContent padding noPaddingTop>
        <Table<SearchCalculation | SearchAdminCalculation>
          filters={filters}
          search={{
            placeholder: i18n.translation.calculations.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              {!moduleId && <TableHeadCell align="left">{i18n.translation.projects.singular}</TableHeadCell>}
              {!moduleId && <TableHeadCell align="left">{i18n.translation.modules.singular}</TableHeadCell>}
              <TableHeadCell align="left">{i18n.translation.common.author}</TableHeadCell>
              {!isAdmin && <TableHeadCell align="left">{i18n.translation.common.shared}</TableHeadCell>}
              <TableHeadCell align="left">{i18n.translation.common.lastModified}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(item) => {
            const isOwn = item.owner?.id === auth.identity?.id;
            return (
              <>
                <TableCell>
                  <Link to={`${urlCalculations}/${item.id}`} onClick={(e) => e.stopPropagation()}>
                    {item.name}
                  </Link>
                </TableCell>
                {!moduleId && <TableCell>{item.project?.name}</TableCell>}
                {!moduleId && (
                  <TableCell>
                    <div className={styles.moduleCell}>
                      <Icon
                        glyph={getCategoryIcon(item.module.category?.code)}
                        className={styles.icon}
                        fill={item.module.category?.color}
                      />
                      <span className={styles.moduleName}>{item.module?.name}</span>
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  {item.owner?.firstName} {item.owner?.lastName}
                </TableCell>
                {!isAdmin && (
                  <TableCell>
                    <SharingIcon isOwn={isOwn} canEdit={item.canEdit} sharedToOthers={item.sharedToOthers} />
                  </TableCell>
                )}
                <TableCell>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</TableCell>
              </>
            );
          }}
          rowActions={rowActions}
          onRowClick={(item) => navigate(`${urlCalculations}/${item.id}`)}
          total={data?.total}
          error={!!error}
        />
        {!!calculationToDelete && (
          <ModalDialog
            onClose={() => setCalculationToDelete(undefined)}
            title={i18n.translation.calculations.delete}
            isSubmitting={isSubmitting}
            buttons={[
              <Button key={0} onClick={() => setCalculationToDelete(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>,
              <Button
                key={1}
                onClick={() => {
                  setIsSubmitting(true);
                  calculationsApi
                    .deleteCalculation(calculationToDelete.project?.id, calculationToDelete.id, isAdmin)
                    .then(() => setCalculationToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}
              >
                {i18n.translation.common.yes}
              </Button>,
            ]}
          >
            {i18n.translation.calculations.questions.delete}
          </ModalDialog>
        )}
        {!!calculationToDuplicate && (
          <DuplicateCalculationDialog
            calculation={calculationToDuplicate}
            onClose={() => setCalculationToDuplicate(undefined)}
            onSave={() => setCalculationToDuplicate(undefined)}
            isAdmin={isAdmin}
          />
        )}
      </FlexContent>

      {!!calculationToShare && (
        <CalculationSharingDialog
          calculationId={calculationToShare.id}
          calculationName={calculationToShare.name}
          isAdmin={isAdmin}
          onClose={() => setCalculationToShare(undefined)}
        />
      )}
      {showModalCreateCalculation && (
        <ModalDialog
          onClose={() => setShowModalCreateCalculation(false)}
          title={i18n.translation.calculations.createNew}
          isSubmitting={isSubmitting}
          buttons={[]}
        >
          <CalculationForm
            isAdmin={isAdmin}
            moduleId={moduleId}
            // disableNavigationOnSuccess
            onSuccess={() => setShowModalCreateCalculation(false)}
            onClose={() => setShowModalCreateCalculation(false)}
          />
        </ModalDialog>
      )}
    </>
  );
};

export default Calculations;
